import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Globe, Mail, Phone, Users, Trophy, Calendar, 
  CheckCircle, Share2, MessageSquare, Plus, Star 
} from 'lucide-react';
import { AZURE } from '../services/api';
import { useSportsLang } from '../contexts/SportsLangContext';

const LABELS = {
  pt: {
    message: 'Mensagem',
    follow: 'Seguir',
    since: 'Desde',
    followers: 'seguidores',
    website: 'Website',
    orgOf: (s) => `Organização de ${s || 'Esporte'} focada em desenvolvimento de atletas e alta performance.`,
    sportSections: 'Seções Esportivas',
    sportSectionsDesc: 'Escolha o departamento que deseja visualizar',
    football: 'Futebol',
    swimming: 'Natação',
    distLabel: 'Distância',
    distAll: 'Todas',
    distSprint: 'Velocidade (50m-100m)',
    distMid: 'Meio Fundo (200m-400m)',
    distLong: 'Fundo (800m-1500m)',
    strokeLabel: 'Especialidade',
    strokeAll: 'Todos os nados',
    clearFilters: 'Limpar Filtros',
    loadingAthletes: 'Carregando atletas...',
    noAthletesFilter: 'Nenhum atleta encontrado com esses filtros.',
    male: 'Masculino',
    female: 'Feminino',
    distSprint_label: 'Velocidade',
    distMid_label: 'Meio Fundo',
    distLong_label: 'Fundo',
    distGeneral: 'Geral',
    multiStroke: 'Multi-nado',
    prev: 'Anterior',
    page: (p, total) => `Página ${p} de ${total}`,
    next: 'Próxima',
    noAthletesListed: 'Nenhum atleta listado.',
    athleteFallback: 'Atleta',
    home: 'Home',
    organizations: 'Organizações',
    tabOverview: 'Visão Geral',
    tabSquad: 'Elenco',
    tabAthletes: 'Atletas',
    tabEvents: 'Eventos & Jogos',
    tabJobs: 'Vagas',
    aboutSection: 'Sobre',
    noDesc: 'Nenhuma descrição disponível.',
    athletesLabel: 'Atletas',
    titlesLabel: 'Títulos',
    rankingLabel: 'Ranking',
    noUpcomingEvents: 'Nenhum evento próximo',
    noUpcomingEventsDesc: 'Os próximos jogos e competições aparecerão aqui.',
    noJobs: 'Nenhuma vaga aberta',
    noJobsDesc: 'Siga a organização para ser notificado de novas oportunidades.',
    infoSection: 'Informações',
    sendEmail: 'Enviar E-mail',
    highlights: 'Destaques',
    seeAll: 'Ver todos',
    topScorer: 'Goleador',
    recordHolder: 'Recordista',
    notFound: 'Organização não encontrada.',
  },
  en: {
    message: 'Message',
    follow: 'Follow',
    since: 'Since',
    followers: 'followers',
    website: 'Website',
    orgOf: (s) => `${s || 'Sports'} organization focused on athlete development and high performance.`,
    sportSections: 'Sports Sections',
    sportSectionsDesc: 'Choose the department you want to view',
    football: 'Football',
    swimming: 'Swimming',
    distLabel: 'Distance',
    distAll: 'All',
    distSprint: 'Sprint (50m-100m)',
    distMid: 'Mid-distance (200m-400m)',
    distLong: 'Distance (800m-1500m)',
    strokeLabel: 'Specialty',
    strokeAll: 'All strokes',
    clearFilters: 'Clear Filters',
    loadingAthletes: 'Loading athletes...',
    noAthletesFilter: 'No athletes found with these filters.',
    male: 'Male',
    female: 'Female',
    distSprint_label: 'Sprint',
    distMid_label: 'Mid-distance',
    distLong_label: 'Distance',
    distGeneral: 'General',
    multiStroke: 'Multi-stroke',
    prev: 'Previous',
    page: (p, total) => `Page ${p} of ${total}`,
    next: 'Next',
    noAthletesListed: 'No athletes listed.',
    athleteFallback: 'Athlete',
    home: 'Home',
    organizations: 'Organizations',
    tabOverview: 'Overview',
    tabSquad: 'Squad',
    tabAthletes: 'Athletes',
    tabEvents: 'Events & Matches',
    tabJobs: 'Jobs',
    aboutSection: 'About',
    noDesc: 'No description available.',
    athletesLabel: 'Athletes',
    titlesLabel: 'Titles',
    rankingLabel: 'Ranking',
    noUpcomingEvents: 'No upcoming events',
    noUpcomingEventsDesc: 'Next matches and competitions will appear here.',
    noJobs: 'No open positions',
    noJobsDesc: 'Follow the organization to be notified of new opportunities.',
    infoSection: 'Information',
    sendEmail: 'Send Email',
    highlights: 'Highlights',
    seeAll: 'See all',
    topScorer: 'Top Scorer',
    recordHolder: 'Record Holder',
    notFound: 'Organization not found.',
  }
};

// === CORES & TEMA ===
const THEME = {
  primary: '#111111',    // Verde Brand
  gold: '#b48c2c',       // Dourado
  bg: '#f3f2ef',         // Fundo estilo LinkedIn
  white: '#ffffff',
  text: '#1f2937',
  textLight: '#6b7280'
};

// === MAPEAMENTO DE CLUBES MULTIESPORTIVOS ===
// Clubes que têm seção de futebol E natação
const MULTISPORT_CLUBS = {
  // Flamengo
  'flamengo': {
    name: 'Flamengo',
    football: { id: 110, name: 'Flamengo' },
    swimming: { id: 53, name: 'Clube de Regatas do Flamengo' },
    swimming_ids: [53, 112]
  },
  // Fluminense
  'fluminense': {
    name: 'Fluminense',
    football: { id: 115, name: 'Fluminense' },
    swimming: { id: 17, name: 'Fluminense FC/RJ' },
    swimming_ids: [17]
  },
  // São Paulo
  // Obs: o id do futebol vem do dataset (ex: export_brasileirao_2025.csv). O id da natação
  // pode variar; quando não conhecido, usamos o id atual quando a página é acessada via natação.
  'sao paulo': {
    name: 'São Paulo',
    football: { id: 121, name: 'Sao Paulo' },
    swimming: { id: null, name: 'São Paulo' }
  },
  // Corinthians (SCCP)
  'corinthians': {
    name: 'Corinthians',
    football: { id: 113, name: 'Corinthians' },
    swimming: { id: 6554, name: 'Corinthians' },
    swimming_ids: [6554, 6561, 6559]
  }
};

// Detecta se clube é multiesportivo
const detectMultisportClub = (clubName) => {
  if (!clubName) return null;
  const normalized = String(clubName)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  // Alias rápido: SCCP
  if (normalized.includes('sccp')) {
    return MULTISPORT_CLUBS['corinthians'] || null;
  }

  for (const [key, data] of Object.entries(MULTISPORT_CLUBS)) {
    if (normalized.includes(key)) {
      return data;
    }
  }
  return null;
};

// === HELPER: LOGO RESOLVER ===
const getLogo = (org) => {
  if (!org) return '/logos-natacao/default-club.svg';
  return org.preferred_logo_url || org.logo_url || org.logo || '/logos-natacao/default-club.svg';
};

// === COMPONENTE: HEADER DO PERFIL (CAPA + INFO) ===
const ProfileHeader = ({ org, sport }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const isVerified = org.is_verified || org.is_national_team;
  const logoSrc = getLogo(org);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
      {/* 1. Capa (Cover Image) */}
      <div className="h-32 md:h-48 bg-gradient-to-r from-gray-800 to-gray-900 relative">
        {/* Aqui poderia vir uma imagem real de capa se a API fornecer */}
        <div className="absolute inset-0 opacity-20 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
      </div>

      {/* 2. Info Principal */}
      <div className="px-6 pb-6 relative">
        
        {/* Logo Flutuante */}
        <div className="-mt-12 md:-mt-16 mb-4 flex justify-between items-end">
          <div className="w-24 h-24 md:w-32 md:h-32 bg-white rounded-xl border-4 border-white shadow-md overflow-hidden flex items-center justify-center">
            <img 
              src={logoSrc} 
              alt={org.name} 
              className="w-full h-full object-contain p-2"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Fallback */}
            <div className="hidden w-full h-full bg-gray-50 items-center justify-center text-2xl font-bold text-gray-400">
              {org.name?.substring(0, 2).toUpperCase()}
            </div>
          </div>

          {/* Ações (Desktop) */}
          <div className="hidden md:flex gap-3 mb-2">
            <button className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors flex items-center gap-2">
              <MessageSquare size={18} /> {t.message}
            </button>
            <button className="px-6 py-2 bg-[#111111] text-white rounded-lg font-bold hover:bg-[#0b0b0c] transition-colors flex items-center gap-2 shadow-sm">
              <Plus size={18} /> {t.follow}
            </button>
          </div>
        </div>

        {/* Textos */}
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{org.name}</h1>
              {isVerified && <CheckCircle size={20} className="text-[#111111]" fill="#f3f4f6" />}
            </div>
            
            <p className="text-gray-600 mb-3 max-w-2xl">
              {org.description || t.orgOf(sport)}
            </p>

            <div className="flex flex-wrap gap-4 text-sm text-gray-500">
              {org.city && (
                <span className="flex items-center gap-1">
                  <MapPin size={16} /> {org.city}, {org.country}
                </span>
              )}
              {org.founded && (
                <span className="flex items-center gap-1">
                  <Calendar size={16} /> {t.since} {org.founded}
                </span>
              )}
              <span className="flex items-center gap-1">
                <Users size={16} /> {org.followers_count || 1240} {t.followers}
              </span>
              {org.website && (
                <a href={org.website} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[#111111] hover:underline">
                  <Globe size={16} /> {t.website}
                </a>
              )}
            </div>
          </div>

          {/* Ações (Mobile) */}
          <div className="flex md:hidden gap-2 mt-2">
            <button className="flex-1 py-2 bg-[#111111] text-white rounded-lg font-bold">{t.follow}</button>
            <button className="px-4 py-2 bg-gray-100 rounded-lg text-gray-600"><MessageSquare size={20}/></button>
            <button className="px-4 py-2 bg-gray-100 rounded-lg text-gray-600"><Share2 size={20}/></button>
          </div>
        </div>

      </div>
    </div>
  );
};

// === COMPONENTE: SPORT SELECTOR (TOGGLE FUTEBOL/NATAÇÃO) ===
const SportSelector = ({ currentSport, clubName, onSportChange, swimId, footId }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const multisportData = detectMultisportClub(clubName);

  // Multisport baseado em query string (quando vindo do merge da OrganizationsPage)
  const hasQueryMultisport = !!(swimId && footId);
  const effective = hasQueryMultisport
    ? {
        name: clubName,
        football: { id: Number(footId), name: clubName },
        swimming: { id: Number(swimId), name: clubName }
      }
    : multisportData;
  
  // Se não é multiesportivo, não mostra o selector
  if (!effective) return null;
  
  const hasFootball = effective.football !== null;
  const hasSwimming = effective.swimming !== null;
  
  // Se só tem 1 esporte, não precisa de toggle
  if (!hasFootball || !hasSwimming) return null;

  const footballTargetId = effective.football?.id;
  const swimmingTargetId = effective.swimming?.id;
  
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bold text-gray-900 mb-1">{t.sportSections}</h3>
          <p className="text-sm text-gray-500">{t.sportSectionsDesc}</p>
        </div>
        
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {hasFootball && (
            <button
              onClick={() => onSportChange('Futebol', footballTargetId)}
              className={`px-6 py-2 rounded-lg font-bold transition-all ${
                currentSport === 'Futebol'
                  ? 'bg-[#111111] text-white shadow-sm'
                  : 'bg-transparent text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.football}
            </button>
          )}
          {hasSwimming && (
            <button
              onClick={() => onSportChange('Natação', swimmingTargetId)}
              className={`px-6 py-2 rounded-lg font-bold transition-all ${
                currentSport === 'Natação'
                  ? 'bg-[#111111] text-white shadow-sm'
                  : 'bg-transparent text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t.swimming}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// === COMPONENTE: LISTA DE ATLETAS COM FILTROS ===
const AthletesTab = ({ orgId, sport }) => {
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    distance: 'ALL',
    stroke: 'ALL'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    page_size: 50,
    total: 0,
    total_pages: 0
  });

  // Carregar atletas do endpoint
  useEffect(() => {
    const fetchAthletes = async () => {
      if (!orgId || sport !== 'Natação') {
        // Para futebol ou outros esportes, usar lista básica (legado)
        return;
      }

      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: pagination.page,
          page_size: pagination.page_size
        });

        if (filters.distance !== 'ALL') {
          params.append('distance_category', filters.distance);
        }

        if (filters.stroke !== 'ALL') {
          params.append('stroke', filters.stroke);
        }

        const response = await AZURE.get(`/swimming/clubs/${orgId}/athletes?${params.toString()}`);
        const data = response.data;

        setAthletes(data.items || []);
        setPagination(prev => ({
          ...prev,
          total: data.total || 0,
          total_pages: data.total_pages || 0
        }));
      } catch (error) {
        console.error('[AthletesTab] Erro ao carregar atletas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAthletes();
  }, [orgId, sport, filters, pagination.page]);

  // Se não for natação, renderiza lista básica (legado)
  if (sport !== 'Natação') {
    return <div className="text-center py-10 text-gray-500">Aba de atletas disponível apenas para natação.</div>;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
      {/* Filtros */}
      <div className="flex flex-wrap gap-4 mb-6 pb-4 border-b border-gray-200">
        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">{t.distLabel}</label>
          <select 
            value={filters.distance}
            onChange={(e) => setFilters(prev => ({ ...prev, distance: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
          >
            <option value="ALL">{t.distAll}</option>
            <option value="rapida">{t.distSprint}</option>
            <option value="media">{t.distMid}</option>
            <option value="longa">{t.distLong}</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-bold text-gray-700 mb-2">{t.strokeLabel}</label>
          <select 
            value={filters.stroke}
            onChange={(e) => setFilters(prev => ({ ...prev, stroke: e.target.value }))}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#111111] focus:border-transparent"
          >
            <option value="ALL">{t.strokeAll}</option>
            <option value="Livre">Livre</option>
            <option value="Costas">Costas</option>
            <option value="Peito">Peito</option>
            <option value="Borboleta">Borboleta</option>
            <option value="Medley">Medley</option>
          </select>
        </div>

        <div className="flex items-end">
          <button 
            onClick={() => setFilters({ distance: 'ALL', stroke: 'ALL' })}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
          >
            {t.clearFilters}
          </button>
        </div>
      </div>

      {/* Lista de Atletas */}
      {loading ? (
        <div className="text-center py-10 text-gray-500">{t.loadingAthletes}</div>
      ) : athletes.length === 0 ? (
        <div className="text-center py-10 text-gray-500">{t.noAthletesFilter}</div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
            {athletes.map((athlete, idx) => (
              <div 
                key={athlete.athlete_id || idx}
                onClick={() => navigate(`/nadador/${athlete.athlete_id}`)}
                className="bg-gray-50 p-4 rounded-lg border border-gray-200 hover:shadow-md transition-all cursor-pointer group"
              >
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-[#111111] text-white flex items-center justify-center text-lg font-bold">
                    {athlete.full_name?.charAt(0) || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-gray-900 truncate group-hover:text-[#111111]">
                      {athlete.full_name}
                    </h4>
                    <p className="text-xs text-gray-500">{athlete.nationality || 'BR'} • {athlete.gender === 'M' ? t.male : t.female}</p>
                  </div>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded font-medium">
                    {athlete.distance_category === 'rapida' ? t.distSprint_label : athlete.distance_category === 'media' ? t.distMid_label : athlete.distance_category === 'longa' ? t.distLong_label : t.distGeneral}
                  </span>
                  <span className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded font-medium">
                    {athlete.strokes_csv || t.multiStroke}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Paginação */}
          {pagination.total_pages > 1 && (
            <div className="flex justify-center gap-2">
              <button 
                onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                disabled={pagination.page === 1}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.prev}
              </button>
              <span className="px-4 py-2 text-gray-700">
                {t.page(pagination.page, pagination.total_pages)}
              </span>
              <button 
                onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages, prev.page + 1) }))}
                disabled={pagination.page === pagination.total_pages}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t.next}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// === COMPONENTE: LISTA DE ATLETAS (CARD GRID) - LEGADO ===
const AthleteGrid = ({ athletes, sport }) => {
  const navigate = useNavigate();
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const isFootball = sport === 'Futebol';

  const getInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    const initials = (first + last).toUpperCase();
    return initials || '?';
  };

  if (!athletes || athletes.length === 0) {
    return <div className="text-center py-10 text-gray-500">{t.noAthletesListed}</div>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {athletes.map((athlete, idx) => {
        const athleteId = athlete.id || athlete.swimmer_id || athlete.player_id || athlete.athlete_id;
        return (
          <div 
            key={athleteId || idx}
            onClick={() => athleteId && navigate(isFootball ? `/jogador/${athleteId}` : `/nadador/${athleteId}`)}
            className="bg-white p-4 rounded-xl border border-gray-200 hover:shadow-md transition-all cursor-pointer flex items-center gap-4 group"
          >
          {/* Avatar */}
          <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden shrink-0 border border-gray-100">
            {isFootball && athlete.photo ? (
              <img src={athlete.photo} alt={athlete.name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-600">
                {getInitials(athlete.name || athlete.full_name)}
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h4 className="font-bold text-gray-900 truncate group-hover:text-[#111111] transition-colors">
              {athlete.name}
            </h4>
            <p className="text-xs text-gray-500 truncate">
              {isFootball ? athlete.position : (athlete.stroke || t.athleteFallback)} • {athlete.nationality || 'BR'}
            </p>
          </div>

          {/* Stats Badge */}
          <div className="text-right">
             {isFootball ? (
               <div className="text-xs font-bold bg-green-50 text-green-700 px-2 py-1 rounded">
                 {athlete.rating || '-'}
               </div>
             ) : (
               <div className="text-xs font-bold bg-blue-50 text-blue-700 px-2 py-1 rounded">
                 Top 10
               </div>
             )}
          </div>
          </div>
        );
      })}
    </div>
  );
};

// === COMPONENTE: LISTA DE ELENCO (FUTEBOL) ===
const FootballRosterList = ({ players }) => {
  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;
  const items = Array.isArray(players) ? players : [];

  const getInitials = (name) => {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    const first = parts[0]?.[0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
    const initials = (first + last).toUpperCase();
    return initials || '?';
  };

  if (!items.length) {
    return <div className="text-center py-10 text-gray-500">{t.noAthletesListed}</div>;
  }

  return (
    <div className="space-y-3">
      {items.map((p, idx) => {
        const id = p.id || p.player_id;
        const name = p.name || p.full_name || t.athleteFallback;
        const position = p.position || p.role || '—';
        const rating = p.rating ?? p.avg_rating;
        const goals = p.goals ?? p.total_goals;
        const matches = p.appearances ?? p.matches ?? p.games;
        const photo = p.photo || p.photo_url || p.avatar_url;

        if (!id) {
          return (
            <div
              key={`no-id-${idx}`}
              className="bg-white border border-gray-200 rounded-xl p-4"
            >
              <div className="font-bold text-gray-900 truncate">{name}</div>
              <div className="text-xs text-gray-500">{position}</div>
            </div>
          );
        }

        return (
          <Link
            key={id}
            to={`/jogador/${id}`}
            className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all flex items-center gap-4 group"
          >
            <div className="w-12 h-12 rounded-full bg-gray-100 overflow-hidden shrink-0 border border-gray-100 flex items-center justify-center">
              {photo ? (
                <img src={photo} alt={name} className="w-full h-full object-cover" />
              ) : (
                <div className="text-sm font-bold text-gray-600">{getInitials(name)}</div>
              )}
            </div>

            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900 truncate group-hover:text-[#111111] transition-colors">
                {name}
              </div>
              <div className="text-xs text-gray-500 truncate">{position}</div>
            </div>

            <div className="flex items-center gap-2">
              {rating != null && rating !== '' && (
                <div className="text-xs font-bold bg-green-50 text-green-700 px-2 py-1 rounded">
                  {String(rating)}
                </div>
              )}
              {goals != null && (
                <div className="text-xs font-bold bg-gray-50 text-gray-700 px-2 py-1 rounded">
                  G {String(goals)}
                </div>
              )}
              {matches != null && (
                <div className="text-xs font-bold bg-gray-50 text-gray-700 px-2 py-1 rounded">
                  J {String(matches)}
                </div>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
};

// === COMPONENTE PRINCIPAL: PAGE ===
const OrganizationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useMemo(() => [new URLSearchParams(window.location.search)], []);
  
  // Estados
  const [loading, setLoading] = useState(true);
  const [orgData, setOrgData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // overview, people, events, jobs
  const [sport, setSport] = useState('Natação'); // Default fallback
  const [orgType, setOrgType] = useState('CLUB'); // CLUB, FEDERATION, BRAND
  const [actualId, setActualId] = useState(id); // ID sem prefixo (swim-, foot-, brand-)
  const [clubName, setClubName] = useState(''); // Nome do clube para detectar multiesportivo
  const [preferredLogoUrl, setPreferredLogoUrl] = useState(null);
  const [linkedSwimIds, setLinkedSwimIds] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get('swim_ids');
      if (!raw) return [];
      return raw.split(',').map(s => Number(String(s).trim())).filter(n => Number.isFinite(n));
    } catch {
      return [];
    }
  });
  const [linkedSwimId, setLinkedSwimId] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('swim_id');
    } catch {
      return null;
    }
  });
  const [linkedFootId, setLinkedFootId] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      return sp.get('foot_id');
    } catch {
      return null;
    }
  });

  const swimIdFromQuery = searchParams.get('swim_id');
  const swimIdsFromQuery = searchParams.get('swim_ids');
  const footIdFromQuery = searchParams.get('foot_id');
  const preferLogo = searchParams.get('prefer_logo');

  const { lang } = useSportsLang();
  const t = LABELS[lang] || LABELS.pt;

  const fetchUnifiedSwimming = async (ids, year = 2025) => {
    const uniqueIds = Array.from(new Set((ids || []).map(n => String(n).trim()).filter(Boolean)));
    const results = await Promise.all(
      uniqueIds.map(clubId => AZURE.get(`/swimming/clubs/${clubId}?year=${year}`).then(r => ({ clubId, data: r.data })).catch(() => null))
    );
    const valid = results.filter(Boolean);
    if (!valid.length) throw new Error('Nenhum perfil de natação retornou dados');

    const pickOrgInfo = (d) => (d?.club || d?.team || (d?.name ? d : {}));
    const pickMembers = (d) => (d?.athletes || d?.players || d?.squad || []);

    let base = pickOrgInfo(valid[0].data);
    for (const v of valid) {
      const info = pickOrgInfo(v.data);
      const hasLogo = !!(info?.logo_url || info?.logo);
      const betterName = (info?.name || '').length > (base?.name || '').length;
      if (hasLogo || betterName) base = { ...base, ...info };
    }

    const normalizeAthleteName = (n = '') => String(n || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, '');

    const seen = new Set();
    const mergedMembers = [];
    for (const v of valid) {
      const members = pickMembers(v.data);
      for (const a of (Array.isArray(members) ? members : [])) {
        const key = a?.id ? `id:${a.id}` : `name:${normalizeAthleteName(a?.name)}`;
        if (!key || key.endsWith(':')) continue;
        if (seen.has(key)) continue;
        seen.add(key);
        mergedMembers.push(a);
      }
    }

    return {
      info: base,
      members: mergedMembers,
      stats: {}
    };
  };

  // Fetch Data
  useEffect(() => {
    const fetchOrg = async () => {
      setLoading(true);
      try {
        console.log('🔍 [OrganizationDetail] ID recebido:', id);
        console.log('🔍 [OrganizationDetail] URL:', window.location.pathname);
        
        // Detectar tipo e esporte pela URL ou pelo ID
        let targetSport = 'Natação';
        let targetType = 'CLUB';
        let extractedId = id;
        
        // Primeiro: detectar pela URL
        const pathname = window.location.pathname;
        if (pathname.includes('/clube/futebol/') || pathname.includes('/futebol/')) {
          targetSport = 'Futebol';
          targetType = 'CLUB';
        } else if (pathname.includes('/clube/natacao/') || pathname.includes('/natacao/')) {
          targetSport = 'Natação';
          targetType = 'CLUB';
        }
        
        // Segundo: detectar pelo prefixo do ID (para casos onde ID vem da OrganizationsPage)
        if (id.startsWith('swim-')) {
          extractedId = id.replace('swim-', '');
          targetSport = 'Natação';
          targetType = 'CLUB';
        } else if (id.startsWith('foot-')) {
          extractedId = id.replace('foot-', '');
          targetSport = 'Futebol';
          targetType = 'CLUB';
        } else if (id.startsWith('brand-')) {
          targetType = 'BRAND';
          // Para marcas/federações, usar dados mock
          const mockData = getMockBrandData(id);
          setOrgData(mockData);
          setSport('Multiesporte');
          setOrgType('BRAND');
          setActualId(id.replace('brand-', ''));
          setLoading(false);
          return;
        }

        // Atualizar states
        setSport(targetSport);
        setOrgType(targetType);
        setActualId(extractedId);

        // Reset de logo preferida quando troca o contexto
        setPreferredLogoUrl(null);

        let response;
        if (targetSport === 'Futebol') {
          console.log(`🏈 [OrganizationDetail] Buscando time de futebol: ${extractedId}`);
          // Use /football/teams para trazer elenco (players)
          response = await AZURE.get(`/football/teams/${extractedId}`);
        } else {
          console.log(`🏊 [OrganizationDetail] Buscando clube de natação: ${extractedId}`);
          // IDs de natação: query string > linkedSwimIds > mapping > id atual
          const idsFromQuery = (swimIdsFromQuery || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean);

          const mappingIds = detectMultisportClub(clubName)?.swimming_ids || [];
          const ids = idsFromQuery.length > 0
            ? idsFromQuery
            : (linkedSwimIds && linkedSwimIds.length > 0
                ? linkedSwimIds
                : (mappingIds.length > 0 ? mappingIds : [String(extractedId)]));

          const uniqueIds = Array.from(new Set(ids.map(String)));
          if (uniqueIds.length > 1) {
            const unified = await fetchUnifiedSwimming(uniqueIds, 2025);
            response = { data: { club: unified.info, athletes: unified.members, statistics: unified.stats } };
          } else {
            response = await AZURE.get(`/swimming/clubs/${extractedId}?year=2025`);
          }
        }

        console.log('✅ [OrganizationDetail] Resposta recebida:', response.data);

        const data = response.data;
        // Normalização de dados (Clube vs Time)
        const orgInfo = data.club || data.team || (data.name ? data : {});
        const members = data.athletes || data.players || data.squad || [];

        // Salvar nome do clube para detectar multiesportivo
        const resolvedName = orgInfo.name || '';
        setClubName(resolvedName);

        // Persistir ids vinculados para permitir alternância interna entre esportes
        const ms = detectMultisportClub(resolvedName);
        if (targetSport === 'Natação') {
          setLinkedSwimId(String(extractedId));
          if (ms?.swimming_ids?.length) setLinkedSwimIds(ms.swimming_ids);
          if (ms?.football?.id) setLinkedFootId(String(ms.football.id));
        } else if (targetSport === 'Futebol') {
          setLinkedFootId(String(extractedId));
          if (ms?.swimming_ids?.length) {
            setLinkedSwimIds(ms.swimming_ids);
            setLinkedSwimId(String(ms.swimming_ids[0]));
          } else if (ms?.swimming?.id) {
            setLinkedSwimId(String(ms.swimming.id));
          }
        }

        setOrgData({
          info: { 
            ...orgInfo, 
            sport: targetSport,
            is_verified: orgInfo.is_verified || orgInfo.is_national_team || false,
            followers_count: members.length ? members.length * 10 : 100
          },
          members: members,
          stats: data.statistics || {}
        });

      } catch (error) {
        console.error("❌ [OrganizationDetail] Erro ao carregar organização:", error);
        // Tentar dados mock em caso de erro
        const mockData = getMockBrandData(id);
        setOrgData(mockData);
        setSport('Multiesporte');
        setOrgType('BRAND');
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [id, searchParams]);

  // Preferir logo do futebol no perfil de natação (quando multisport)
  useEffect(() => {
    const multisportData = detectMultisportClub(clubName);
    const resolvedFootId = footIdFromQuery || linkedFootId || multisportData?.football?.id;
    const shouldPrefer = String(preferLogo || '').toLowerCase() === 'football';

    if (!resolvedFootId) return;
    if (sport !== 'Natação') return;

    let cancelled = false;
    const fetchLogo = async () => {
      try {
        const response = await AZURE.get(`/football/teams/${resolvedFootId}`);
        const data = response.data;
        const orgInfo = data.club || data.team || (data.name ? data : {});
        const logo = orgInfo.logo_url || orgInfo.logo;
        if (!cancelled && logo && (shouldPrefer || !preferredLogoUrl)) {
          setPreferredLogoUrl(logo);
        }
      } catch (e) {
        // ignore
      }
    };

    fetchLogo();
    return () => {
      cancelled = true;
    };
  }, [sport, clubName, footIdFromQuery, linkedFootId, preferLogo, preferredLogoUrl]);

  // Handler para trocar de esporte (para clubes multiesportivos)
  const handleSportChange = async (newSport, newId) => {
    console.log(`🔄 [SportSelector] Trocando para ${newSport}, ID: ${newId}`);

    if (!newId) {
      console.warn('[SportSelector] ID ausente para troca de esporte.');
      return;
    }
    
    setLoading(true);
    setSport(newSport);
    setActualId(newId);

    if (newSport === 'Futebol') setLinkedFootId(String(newId));
    if (newSport === 'Natação') setLinkedSwimId(String(newId));
    
    try {
      let response;
      if (newSport === 'Futebol') {
        response = await AZURE.get(`/football/teams/${newId}`);
      } else {
        // Se temos múltiplos perfis de natação para o clube, unificar ao trocar
        const ms = detectMultisportClub(clubName);
        const ids = (swimIdsFromQuery || '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        const mappingIds = ms?.swimming_ids || [];

        const resolved = ids.length > 0
          ? ids
          : (linkedSwimIds && linkedSwimIds.length > 0
              ? linkedSwimIds
              : (mappingIds.length > 0 ? mappingIds : [String(newId)]));

        const uniqueIds = Array.from(new Set(resolved.map(String)));
        if (uniqueIds.length > 1) {
          const unified = await fetchUnifiedSwimming(uniqueIds, 2025);
          response = { data: { club: unified.info, athletes: unified.members, statistics: unified.stats } };
        } else {
          response = await AZURE.get(`/swimming/clubs/${newId}?year=2025`);
        }
      }

      const data = response.data;
      const orgInfo = data.club || data.team || (data.name ? data : {});
      const members = data.athletes || data.players || data.squad || [];

      setOrgData({
        info: { 
          ...orgInfo, 
          sport: newSport,
          is_verified: orgInfo.is_verified || orgInfo.is_national_team || false,
          followers_count: members.length ? members.length * 10 : 100
        },
        members: members,
        stats: data.statistics || {}
      });
    } catch (error) {
      console.error(`❌ Erro ao trocar para ${newSport}:`, error);
    } finally {
      setLoading(false);
    }
  };

  // Função auxiliar para dados mock de marcas/federações
  const getMockBrandData = (brandId) => {
    const mockBrands = {
      'brand-1': {
        info: {
          name: 'Confederação Brasileira de Desportos Aquáticos',
          logo_url: '/logos-natacao/cbda.png',
          description: 'Entidade máxima da natação brasileira, responsável pela organização e promoção do esporte no país.',
          city: 'Rio de Janeiro',
          country: 'Brasil',
          founded: '1898',
          website: 'https://cbda.org.br',
          is_verified: true,
          followers_count: 50000,
          sport: 'Natação'
        },
        members: [],
        stats: {}
      },
      'brand-2': {
        info: {
          name: 'Confederação Brasileira de Futebol',
          logo_url: '/logos/cbf.png',
          description: 'Entidade máxima do futebol brasileiro.',
          city: 'Rio de Janeiro',
          country: 'Brasil',
          founded: '1914',
          website: 'https://cbf.com.br',
          is_verified: true,
          followers_count: 1000000,
          sport: 'Futebol'
        },
        members: [],
        stats: {}
      },
      'brand-3': {
        info: {
          name: 'Nike Brasil',
          logo_url: 'https://logo.clearbit.com/nike.com',
          description: 'Fornecedora oficial de material esportivo para atletas e clubes.',
          country: 'Global',
          website: 'https://nike.com.br',
          is_verified: true,
          followers_count: 250000,
          sport: 'Multiesporte'
        },
        members: [],
        stats: {}
      },
      'brand-4': {
        info: {
          name: 'Speedo Brasil',
          logo_url: 'https://logo.clearbit.com/speedo.com',
          description: 'Tecnologia para alta performance na água.',
          country: 'Brasil',
          website: 'https://speedo.com.br',
          is_verified: true,
          followers_count: 80000,
          sport: 'Natação'
        },
        members: [],
        stats: {}
      }
    };

    return mockBrands[brandId] || {
      info: {
        name: 'Organização Não Encontrada',
        description: 'Esta organização não foi encontrada.',
        is_verified: false,
        followers_count: 0
      },
      members: [],
      stats: {}
    };
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-12 h-12 border-4 border-t-[#111111] border-gray-200 rounded-full"></div></div>;
  if (!orgData) return <div className="text-center py-20">{t.notFound}</div>;

  return (
    <div className="min-h-screen pb-12" style={{ backgroundColor: THEME.bg }}>
      
      {/* 1. Header Global (Navegação) */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-sm px-4 py-3 mb-6">
        <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-gray-900">{t.home}</Link> / 
          <Link to="/clubes" className="hover:text-gray-900">{t.organizations}</Link> / 
          <span className="font-bold text-gray-900">{orgData.info.name}</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4">
        
        {/* 2. Header do Perfil */}
        <ProfileHeader org={{ ...orgData.info, preferred_logo_url: preferredLogoUrl || orgData.info.preferred_logo_url }} sport={sport} />

        {/* 2.5. Sport Selector (para clubes multiesportivos) */}
        <SportSelector 
          currentSport={sport}
          clubName={clubName}
          onSportChange={handleSportChange}
          swimId={(swimIdFromQuery || (swimIdsFromQuery ? swimIdsFromQuery.split(',')[0]?.trim() : null) || linkedSwimId || (sport === 'Natação' ? actualId : null))}
          footId={footIdFromQuery || linkedFootId || detectMultisportClub(clubName)?.football?.id || (sport === 'Futebol' ? actualId : null)}
        />

        {/* 3. Navegação Interna (Abas) */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-2 mb-6 sticky top-20 z-20">
          <div className="flex gap-6 overflow-x-auto">
            {[
              { id: 'overview', label: t.tabOverview },
              { id: 'people', label: sport === 'Futebol' ? t.tabSquad : t.tabAthletes },
              { id: 'events', label: t.tabEvents },
              { id: 'jobs', label: t.tabJobs }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-4 px-2 text-sm font-bold border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id 
                    ? 'border-[#111111] text-[#111111]' 
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* 4. Conteúdo das Abas */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Coluna Principal (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            
            {activeTab === 'overview' && (
              <>
                {/* Sobre */}
                <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                  <h3 className="font-bold text-lg text-gray-900 mb-4">{t.aboutSection}</h3>
                  <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                    {orgData.info.about || orgData.info.description || t.noDesc}
                  </p>
                </div>

                {/* Destaques (Stats Rápidos) */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                   <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
                      <div className="text-2xl font-bold text-[#111111]">{orgData.members.length}</div>
                      <div className="text-xs text-gray-500 font-bold uppercase">{t.athletesLabel}</div>
                   </div>
                   <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
                      <div className="text-2xl font-bold text-[#b48c2c]">12</div>
                      <div className="text-xs text-gray-500 font-bold uppercase">{t.titlesLabel}</div>
                   </div>
                   <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm text-center">
                      <div className="text-2xl font-bold text-blue-600">Top 5</div>
                      <div className="text-xs text-gray-500 font-bold uppercase">{t.rankingLabel}</div>
                   </div>
                </div>
              </>
            )}

            {activeTab === 'people' && (
              sport === 'Natação' ? (
                <AthletesTab orgId={actualId} sport={sport} />
              ) : sport === 'Futebol' ? (
                <FootballRosterList players={orgData.members} />
              ) : (
                <AthleteGrid athletes={orgData.members} sport={sport} />
              )
            )}

            {activeTab === 'events' && (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                  <Calendar size={32} />
                </div>
                <h3 className="font-bold text-gray-900">{t.noUpcomingEvents}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.noUpcomingEventsDesc}</p>
              </div>
            )}

            {activeTab === 'jobs' && (
              <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4 text-gray-400">
                  <Star size={32} />
                </div>
                <h3 className="font-bold text-gray-900">{t.noJobs}</h3>
                <p className="text-gray-500 text-sm mt-1">{t.noJobsDesc}</p>
              </div>
            )}

          </div>

          {/* Coluna Lateral (1/3) */}
          <div className="space-y-6">
            
            {/* Contato Rápido */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <h3 className="font-bold text-sm text-gray-900 uppercase tracking-wide mb-4">{t.infoSection}</h3>
              <div className="space-y-3 text-sm">
                {orgData.info.website && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Globe size={16} />
                    <a href={orgData.info.website} target="_blank" rel="noreferrer" className="hover:text-[#111111] truncate">
                      {orgData.info.website.replace('https://', '')}
                    </a>
                  </div>
                )}
                {orgData.info.email && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Mail size={16} />
                    <a href={`mailto:${orgData.info.email}`} className="hover:text-[#111111] truncate">
                      {t.sendEmail}
                    </a>
                  </div>
                )}
                {orgData.info.phone && (
                  <div className="flex items-center gap-3 text-gray-600">
                    <Phone size={16} />
                    <span>{orgData.info.phone}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Atletas em Destaque (Mini Feed) */}
            <div className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold text-sm text-gray-900 uppercase tracking-wide">{t.highlights}</h3>
                <Link to="#" className="text-xs font-bold text-[#111111] hover:underline">{t.seeAll}</Link>
              </div>
              <div className="space-y-3">
                {orgData.members.slice(0, 3).map((a, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-xs">
                      {String(a.name || a.full_name || '').charAt(0) || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">{a.name || a.full_name || t.athleteFallback}</p>
                      <p className="text-xs text-gray-500 truncate">{sport === 'Futebol' ? t.topScorer : t.recordHolder}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>

        </div>

      </div>
    </div>
  );
};

export default OrganizationDetailPage;
